// Composable para exportar a Excel con formato corporativo Kompara/Visaovip.
// Usa xlsx-js-style (fork de SheetJS con soporte de estilos).
//
// Uso:
//   import { exportarAExcel } from '@/composables/useExcelExport';
//   exportarAExcel({
//     headers: ['Codigo', 'Producto', 'Tu Precio', 'Competidor', 'Diferencia'],
//     rows: [['57248', 'Notebook XPS', 1200, 950, '+26%'], ...],
//     priceColumns: [2, 3],   // índices (0-based) de columnas con precios para colorear el menor
//     filename: 'precios_altos_alborada_2026-04-21.xlsx',
//     sheetName: 'Precios Altos'
//   });
//
// Formato aplicado:
//   - Header: fondo azul #4472C4, texto blanco, bold
//   - Datos: texto negro, sin fondo
//   - Precio más bajo de la fila: fondo azul claro #DBEAFE + bold
//   - Anchos auto-ajustados según headers

import * as XLSX from 'xlsx-js-style';

const HEADER_FILL = { fgColor: { rgb: '4472C4' } };
const HEADER_FONT = { color: { rgb: 'FFFFFF' }, bold: true, name: 'Calibri', sz: 11 };
const HEADER_ALIGN = { horizontal: 'center', vertical: 'center' };

const MIN_PRICE_FILL = { fgColor: { rgb: 'DBEAFE' } };
const MIN_PRICE_FONT = { bold: true, name: 'Calibri', sz: 11, color: { rgb: '000000' } };

const DEFAULT_FONT = { name: 'Calibri', sz: 11, color: { rgb: '000000' } };
const BORDER = {
  top: { style: 'thin', color: { rgb: 'D0D0D0' } },
  bottom: { style: 'thin', color: { rgb: 'D0D0D0' } },
  left: { style: 'thin', color: { rgb: 'D0D0D0' } },
  right: { style: 'thin', color: { rgb: 'D0D0D0' } }
};

/**
 * Convierte un valor a número si es posible (para encontrar mínimos).
 * Soporta strings con coma decimal y símbolos.
 */
function toNumber(v) {
  if (v === null || v === undefined || v === '') return null;
  if (typeof v === 'number') return v;
  const s = String(v).replace(/[$\s]/g, '').replace(',', '.');
  const n = parseFloat(s);
  return isNaN(n) ? null : n;
}

/**
 * Encuentra el índice de la columna con el menor precio entre las priceColumns.
 * Devuelve null si no hay precios válidos.
 */
function findMinPriceColIdx(row, priceColumns) {
  let minIdx = null;
  let minVal = null;
  for (const colIdx of priceColumns) {
    const n = toNumber(row[colIdx]);
    if (n === null || n <= 0) continue;
    if (minVal === null || n < minVal) {
      minVal = n;
      minIdx = colIdx;
    }
  }
  return minIdx;
}

/**
 * Calcula ancho aproximado de columna basado en el contenido más largo.
 * Mínimo 10, máximo 60.
 */
function calcColWidths(headers, rows) {
  return headers.map((h, idx) => {
    const headerLen = String(h || '').length;
    let maxDataLen = 0;
    for (const row of rows) {
      const val = row[idx];
      const len = val !== null && val !== undefined ? String(val).length : 0;
      if (len > maxDataLen) maxDataLen = len;
    }
    const width = Math.min(60, Math.max(10, Math.max(headerLen, maxDataLen) + 2));
    return { wch: width };
  });
}

/**
 * Exporta los datos a un archivo .xlsx con formato corporativo.
 *
 * @param {Object} opts
 * @param {string[]} opts.headers - Encabezados de columna
 * @param {Array<Array>} opts.rows - Filas de datos (cada fila es un array)
 * @param {number[]} [opts.priceColumns=[]] - Índices (0-based) de columnas con precios; el menor se pinta de azul claro
 * @param {string} [opts.filename='export.xlsx'] - Nombre del archivo descargado
 * @param {string} [opts.sheetName='Datos'] - Nombre de la hoja
 */
export function exportarAExcel({ headers, rows, priceColumns = [], filename = 'export.xlsx', sheetName = 'Datos' }) {
  if (!headers || !Array.isArray(headers) || headers.length === 0) {
    console.error('exportarAExcel: headers vacíos');
    return;
  }
  if (!rows || !Array.isArray(rows)) {
    console.error('exportarAExcel: rows debe ser array');
    return;
  }

  // Construir worksheet desde array de arrays (header + data)
  const aoa = [headers, ...rows];
  const ws = XLSX.utils.aoa_to_sheet(aoa);

  // Estilo del header (fila 1)
  for (let c = 0; c < headers.length; c++) {
    const cellRef = XLSX.utils.encode_cell({ r: 0, c });
    if (!ws[cellRef]) continue;
    ws[cellRef].s = {
      fill: HEADER_FILL,
      font: HEADER_FONT,
      alignment: HEADER_ALIGN,
      border: BORDER
    };
  }

  // Estilo de filas de datos
  for (let r = 0; r < rows.length; r++) {
    const row = rows[r];
    const minColIdx = priceColumns.length > 0 ? findMinPriceColIdx(row, priceColumns) : null;

    for (let c = 0; c < headers.length; c++) {
      const cellRef = XLSX.utils.encode_cell({ r: r + 1, c });
      if (!ws[cellRef]) {
        // Crear celda vacía si no existe (para que el border se aplique)
        ws[cellRef] = { v: '', t: 's' };
      }
      const isMin = c === minColIdx;
      ws[cellRef].s = {
        fill: isMin ? MIN_PRICE_FILL : undefined,
        font: isMin ? MIN_PRICE_FONT : DEFAULT_FONT,
        border: BORDER,
        alignment: priceColumns.includes(c)
          ? { horizontal: 'right' }
          : (c === 0 ? { horizontal: 'center' } : { horizontal: 'left' })
      };
    }
  }

  // Anchos de columna
  ws['!cols'] = calcColWidths(headers, rows);

  // Freeze pane: header
  ws['!freeze'] = { xSplit: 0, ySplit: 1 };

  // Construir workbook y descargar
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  XLSX.writeFile(wb, filename);
}

/**
 * Helper: descarga CSV desde array de arrays (igual a las funciones existentes pero centralizado).
 */
export function exportarACsv({ headers, rows, filename = 'export.csv' }) {
  const all = [headers, ...rows];
  const csv = all.map(r => r.map(c => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
